import { Fragment, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import classNames from 'classnames';
import { CandleValidation } from 'client/voladeum/candle-validations/types';
import { FORMAT_TYPE } from 'cyprus/enums';
import { formatValue } from 'cyprus/formats';
import { selectCandleValidations } from 'ducks/candle-validations/selectors';
import { selectCandleWorkers } from 'ducks/candle-workers/selectors';
import { Tooltip } from 'framework/Tooltip';
import moment from 'moment';
import { useAppSelector } from 'store/hooks';
import { mergeQuery } from 'utils/queries';

import { Card } from 'components/cards/Card';

import { CandleTooltip } from './CandleTooltip';

import cn from './CandleWorkersHeatmap.module.scss';

export const CandleWorkersHeatmap = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const candle_validations = useAppSelector(selectCandleValidations);
  const candle_workers = useAppSelector(selectCandleWorkers);

  const window_start = moment().utc().startOf('month').subtract(3, 'months');
  const window_end = moment().utc().add(1, 'days').startOf('day');

  const validations_map = useMemo(() => {
    const map = new Map<number, (typeof candle_validations)[number]>();
    for (const validation of candle_validations) {
      const ts = moment(validation.since_date).utc().startOf('day');
      if (ts.isBetween(window_start, window_end, null, '[)')) {
        map.set(ts.valueOf(), validation);
      }
    }
    return map;
  }, [candle_validations, window_start, window_end]);

  const worker_map = useMemo(() => {
    const map = new Map<number, (typeof candle_workers)[number]>();
    for (const worker of candle_workers) {
      const ts = moment(worker.since_date).utc().startOf('day');
      if (ts.isBetween(window_start, window_end, null, '[)')) {
        map.set(ts.valueOf(), worker);
      }
    }
    return map;
  }, [candle_workers, window_start, window_end]);

  const months = useMemo(() => {
    const arr: moment.Moment[] = [];
    for (let i = 3; i >= 0; i--) {
      arr.push(moment().utc().startOf('month').subtract(i, 'months'));
    }
    return arr;
  }, []);

  const max_days = useMemo(() => Math.max(...months.map((m) => m.daysInMonth())), [months]);

  const onClick = useCallback(
    (validation?: CandleValidation) => {
      if (validation?.since_milliseconds && validation?.to_milliseconds) {
        const { since_milliseconds, to_milliseconds } = validation;
        navigate(`${pathname}${mergeQuery(search, { since_milliseconds, to_milliseconds })}`);
      }
    },
    [navigate, pathname, search]
  );
  return (
    <Card fit>
      <div className={cn.content}>
        <h2>Candle Workers</h2>
        <div
          className={cn.grid}
          style={{ gridTemplateColumns: `auto repeat(${max_days}, minmax(0, 1fr))` }}
        >
          {months.map((month, idx) => {
            const label = month.format('MMM');

            return (
              <Fragment key={idx}>
                <div className={cn.hour}>
                  <span>{label}</span>
                </div>
                {Array.from({ length: max_days }).map((_, i) => {
                  const day = i + 1;
                  const unix = month.clone().date(day).valueOf();

                  const validation_exists = validations_map.has(unix);
                  const worker_exists = worker_map.has(unix);
                  const title = formatValue(unix, { format: FORMAT_TYPE.UNIX, utc: true });

                  if (day > month.daysInMonth()) {
                    return <div key={`blank-${idx}-${i}`} className={cn.cell} />;
                  }

                  if (unix >= window_start.valueOf() && unix < window_end.valueOf()) {
                    return (
                      <Tooltip
                        key={unix}
                        information={
                          <CandleTooltip
                            candle_validation={validations_map.get(unix)}
                            candle_worker={worker_map.get(unix)}
                            title={title}
                          />
                        }
                      >
                        <div
                          onClick={() => onClick(validations_map.get(unix))}
                          className={classNames(
                            cn.cell,
                            cn.exists,
                            'transition',
                            { [cn.validation]: validation_exists },
                            { [cn.worker]: worker_exists }
                          )}
                          title={title}
                        />
                      </Tooltip>
                    );
                  }
                  return <div key={`blank-${idx}-${i}`} className={cn.cell} />;
                })}
              </Fragment>
            );
          })}
        </div>
      </div>
    </Card>
  );
};
